import React, { useState, useMemo, useEffect } from 'react';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command';
import { useMacros, type Macro } from '@/hooks/useMacros';
import { Sparkles, Plus, Globe, User } from 'lucide-react';
import { MacroDialog } from './MacroDialog';
import { createPortal } from 'react-dom';

interface MacroMenuProps {
  open: boolean;
  onSelect: (macroContent: string) => void;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLElement>;
  searchQuery?: string;
}

export function MacroMenu({ open, onSelect, onClose, triggerRef, searchQuery = '', onCreateNew }: MacroMenuProps) {
  const { globalMacros, individualMacros, isLoading } = useMacros();
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  // Calcular posição do menu baseado no input
  useEffect(() => {
    if (open && triggerRef.current) {
      const updatePosition = () => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (rect) {
          setPosition({
            top: rect.top + window.scrollY - 10,
            left: rect.left + window.scrollX,
            width: rect.width,
          });
        }
      };

      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [open, triggerRef]);

  // Usar useMemo ao invés de useEffect + useState para evitar loops
  const filteredGlobalMacros = useMemo(() => {
    if (!searchQuery) return globalMacros;
    
    const query = searchQuery.toLowerCase();
    return globalMacros.filter(
      (m) =>
        m.title.toLowerCase().includes(query) ||
        m.content.toLowerCase().includes(query)
    );
  }, [searchQuery, globalMacros]);

  const filteredIndividualMacros = useMemo(() => {
    if (!searchQuery) return individualMacros;
    
    const query = searchQuery.toLowerCase();
    return individualMacros.filter(
      (m) =>
        m.title.toLowerCase().includes(query) ||
        m.content.toLowerCase().includes(query)
    );
  }, [searchQuery, individualMacros]);

  const handleMacroSelect = (macro: Macro) => {
    onSelect(macro.content);
    onClose();
  };

  const handleCreateNew = () => {
    // Chamar callback do pai para abrir o diálogo
    if (onCreateNew) {
      onCreateNew();
    }
    // Fechar o menu
    onClose();
  };

  const totalMacros = filteredGlobalMacros.length + filteredIndividualMacros.length;

  // Não renderizar nada se não estiver aberto
  if (!open) return null;

  const menuContent = (
    <div
      className="fixed z-[100] w-[400px] bg-popover border rounded-md shadow-lg"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        transform: 'translateY(-100%)',
        maxHeight: '400px',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Command>
        <CommandList>
          {/* Global Macros */}
          {filteredGlobalMacros.length > 0 && (
            <>
              <CommandGroup heading={
                <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground px-2 py-1.5">
                  <Globe className="h-3 w-3" />
                  Macros Globais
                </div>
              }>
                {filteredGlobalMacros.map((macro) => (
                  <CommandItem
                    key={macro.id}
                    onSelect={() => handleMacroSelect(macro)}
                    className="cursor-pointer"
                  >
                    <div className="flex flex-col gap-1 flex-1">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-3.5 w-3.5 text-primary" />
                        <span className="font-medium text-sm">{macro.title}</span>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 ml-5">
                        {macro.content}
                      </p>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
              {filteredIndividualMacros.length > 0 && <CommandSeparator />}
            </>
          )}

          {/* Individual Macros */}
          {filteredIndividualMacros.length > 0 && (
            <CommandGroup heading={
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground px-2 py-1.5">
                <User className="h-3 w-3" />
                Minhas Macros
              </div>
            }>
              {filteredIndividualMacros.map((macro) => (
                <CommandItem
                  key={macro.id}
                  onSelect={() => handleMacroSelect(macro)}
                  className="cursor-pointer"
                >
                  <div className="flex flex-col gap-1 flex-1">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-3.5 w-3.5 text-primary" />
                      <span className="font-medium text-sm">{macro.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2 ml-5">
                      {macro.content}
                    </p>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {/* Empty State */}
          {!isLoading && totalMacros === 0 && (
            <CommandEmpty>
              <div className="text-center py-6">
                <Sparkles className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  {searchQuery ? 'Nenhuma macro encontrada' : 'Nenhuma macro cadastrada'}
                </p>
              </div>
            </CommandEmpty>
          )}

          {/* Create New Button */}
          <CommandSeparator />
          <CommandGroup>
            <CommandItem
              onSelect={handleCreateNew}
              className="cursor-pointer text-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              <span className="font-medium">Cadastrar nova macro</span>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </div>
  );

  return createPortal(menuContent, document.body);
}
